/**
 * LogoOverlay — mirrors RouteOverlay's chrome (modal, top pill row, bottom
 * mode selector, close X). Browses the logo registry; lets you download
 * every variant of every logo.
 */
import { Modal, Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";
import { SvgXml } from "react-native-svg";

import { COLORS } from "../../lib/brand";
import { downloadLogo, LOGOS, type Logo, type LogoVariant } from "../../lib/logos";

/* ─────────────────────── Palette ─────────────────────── */

const PAGE_BG = "#141414";
const HEADER_BG = "#1A1A1A";
const HEADER_BORDER = "rgba(181,135,58,0.55)";
const BUTTON_BORDER = "rgba(247,244,238,0.14)";
const BUTTON_HOVER_BG = "rgba(247,244,238,0.06)";
const TEXT = COLORS.LINEN.hex;
const TEXT_DIM = "rgba(247,244,238,0.66)";
const AMBER = COLORS.AMBER.hex;
const AMBER_BORDER_ACTIVE = "rgba(181,135,58,0.55)";

/* ─────────────────────── Overlay ─────────────────────── */

export function LogoOverlay({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.75)",
          padding: 24,
          ...(Platform.OS === "web"
            ? ({ backdropFilter: "blur(12px)" } as unknown as object)
            : null),
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={[
            {
              flex: 1,
              backgroundColor: PAGE_BG,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: HEADER_BORDER,
              overflow: "hidden",
              position: "relative",
            },
            Platform.OS === "web"
              ? ({ boxShadow: "0 32px 80px rgba(0,0,0,0.55)" } as unknown as ViewStyle)
              : null,
          ]}
        >
          {/* Close */}
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            style={({ hovered }) => [
              {
                position: "absolute",
                top: 16,
                right: 16,
                width: 30,
                height: 30,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: BUTTON_BORDER,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 220,
                backgroundColor: HEADER_BG,
              },
              Platform.OS === "web" && hovered
                ? ({ backgroundColor: BUTTON_HOVER_BG } as ViewStyle)
                : null,
            ]}
          >
            <Text style={{ fontSize: 15, color: TEXT_DIM, lineHeight: 15 }}>×</Text>
          </Pressable>

          {/* Body — single scrollable list */}
          <ScrollView
            style={{ flex: 1, width: "100%" }}
            contentContainerStyle={{ paddingHorizontal: 32, paddingTop: 56, paddingBottom: 32 }}
          >
            <View style={{ flexDirection: "row", flexWrap: "wrap", marginHorizontal: -8 }}>
              {LOGOS.map((logo) => (
                <View
                  key={logo.id}
                  style={{
                    width: logo.aspect === "social" ? "100%" : "50%",
                    padding: 8,
                  }}
                >
                  <LogoCard logo={logo} />
                </View>
              ))}
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/* ─────────────────────── Logo card ─────────────────────── */

function LogoCard({ logo }: { logo: Logo }) {
  const previewBg =
    logo.background === "light"
      ? COLORS.LINEN.hex
      : logo.background === "dark"
        ? COLORS.FOREST_DEEP.hex
        : logo.background === "transparent"
          ? "transparent"
          : "#1A1A1A";

  const previewHeight = logo.aspect === "social" ? 320 : logo.aspect === "wide" ? 140 : 200;
  const svgVariant = logo.variants.find((v) => v.format === "svg");
  const pngVariant = pickLargestPng(logo.variants);

  return (
    <View
      style={{
        backgroundColor: HEADER_BG,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: BUTTON_BORDER,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          height: previewHeight,
          backgroundColor: previewBg,
          alignItems: "center",
          justifyContent: "center",
          padding: 18,
          ...(logo.background === "transparent"
            ? ({
                backgroundImage:
                  "linear-gradient(45deg, rgba(247,244,238,0.04) 25%, transparent 25%, transparent 75%, rgba(247,244,238,0.04) 75%, rgba(247,244,238,0.04)), linear-gradient(45deg, rgba(247,244,238,0.04) 25%, transparent 25%, transparent 75%, rgba(247,244,238,0.04) 75%, rgba(247,244,238,0.04))",
                backgroundSize: "20px 20px",
                backgroundPosition: "0 0, 10px 10px",
              } as unknown as ViewStyle)
            : null),
        }}
      >
        <LogoPreview svg={logo.svg} aspect={logo.aspect} />
      </View>

      <View
        style={{
          padding: 16,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          style={{
            fontFamily: "DMSans_600SemiBold",
            fontSize: 13,
            color: TEXT,
            flex: 1,
          }}
          numberOfLines={1}
        >
          {logo.name}
        </Text>
        <View style={{ flexDirection: "row", gap: 6 }}>
          {svgVariant ? (
            <DownloadPill label="SVG" onPress={() => downloadLogo(logo, svgVariant)} />
          ) : null}
          {pngVariant ? (
            <DownloadPill label="PNG" onPress={() => downloadLogo(logo, pngVariant)} />
          ) : null}
        </View>
      </View>
    </View>
  );
}

function pickLargestPng(variants: LogoVariant[]): LogoVariant | undefined {
  const pngs = variants.filter((v) => v.format === "png");
  if (pngs.length === 0) return undefined;
  return pngs.reduce((largest, v) =>
    (v.pxSize ?? 0) > (largest.pxSize ?? 0) ? v : largest,
  );
}

function LogoPreview({ svg, aspect }: { svg: string; aspect: Logo["aspect"] }) {
  const width = aspect === "social" ? 480 : aspect === "wide" ? 280 : 120;
  const height = aspect === "social" ? 252 : aspect === "wide" ? 56 : 120;
  return <SvgXml xml={svg} width={width} height={height} />;
}

function DownloadPill({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      style={({ hovered }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          paddingHorizontal: 10,
          paddingVertical: 6,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: BUTTON_BORDER,
        },
        Platform.OS === "web" && hovered
          ? ({
              backgroundColor: BUTTON_HOVER_BG,
              borderColor: AMBER_BORDER_ACTIVE,
              cursor: "pointer",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <DownloadIcon />
      <Text
        style={{
          fontFamily: "DMSans_600SemiBold",
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function DownloadIcon() {
  return (
    <SvgXml
      xml={`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none"><path d="M6 1 V8 M3 5.5 L6 8 L9 5.5 M2 10 H10" stroke="${AMBER}" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`}
      width={11}
      height={11}
    />
  );
}

